// TIP-493 (MV Stage 4b) — "Buy a Web Profile" plan selection (step 1 of 3).
//
// Non-member: a two-card comparison — pay the standard rate, or take the Member
// rate by joining Membership in the same transaction (annual/monthly). Member:
// the Member rate is applied directly (no membership card). The chosen option is
// handed to /plans/paymentdetails, which drives create_payment.
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetCompanyQuery } from "../../../store/Company/companyApiSlice";
import { selectActiveCompany } from "../../../store/activeCompanySlice";
import {
  buildWebProfileSummary,
  WEB_PROFILE_STANDARD,
  WEB_PROFILE_MEMBER,
  MEMBERSHIP_ANNUAL,
  MEMBERSHIP_MONTHLY,
  PLAN_YEARLY,
  PLAN_MONTHLY,
  PROFILE_WINDOW_DAYS,
} from "../../../constants/webProfilePricing";
import { WebProfileSummary } from "./WebProfileSummary";
import { Aed } from "../../common/Money";

const PROFILE_FEATURES = [
  "Identity, registration, signatures",
  "Banking footprint and recent filings",
  `${PROFILE_WINDOW_DAYS} days of live updates inside the window`,
];

const MEMBERSHIP_FEATURES = [
  "Web Profile at the Member rate (10% off)",
  "Payment Endorsements and the Partner Program",
  "Path to the Verified Member badge",
];

const FeatureList = ({ features }) => (
  <ul className="space-y-2 mt-3">
    {features.map((f) => (
      <li key={f} className="flex gap-2 text-f14 text-primaryText">
        <span className="text-link mt-[2px]">•</span>
        <span>{f}</span>
      </li>
    ))}
  </ul>
);

const RadioDot = ({ selected }) => (
  <span
    aria-hidden="true"
    className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
      selected ? "border-primary bg-button" : "border-gray-300"
    }`}
  >
    {selected && <span className="text-white text-f12 leading-none">✓</span>}
  </span>
);

export const PaymentPlans = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useSelector((s) => s.auth);
  const activeCompany = useSelector(selectActiveCompany);

  const stateCompanyId = location.state?.company_id;
  const storedCompanyId = (() => {
    try {
      return JSON.parse(localStorage.getItem("payasyougoCompanyID"));
    } catch (e) {
      return null;
    }
  })();
  const targetCompanyId = stateCompanyId || storedCompanyId;

  const { data: company } = useGetCompanyQuery(
    { token, companyId: targetCompanyId },
    { skip: !targetCompanyId }
  );

  // TIP-493 (W3) — the cached active company's profile_status can be stale (e.g.
  // right after buying membership). Fetch the acting company's CURRENT status
  // from the backend and prefer it; fall back to the cached value.
  const activeCompanyId = activeCompany?.companyID;
  const { data: actingCompany } = useGetCompanyQuery(
    { token, companyId: activeCompanyId },
    { skip: !activeCompanyId }
  );
  const actingProfileStatus =
    actingCompany?.profile_status || activeCompany?.profile_status;
  const isMember = ["member", "verified"].includes(actingProfileStatus);

  const [mode, setMode] = useState(isMember ? "member" : "payg");
  const [plan, setPlan] = useState(PLAN_YEARLY);

  useEffect(() => {
    if (isMember) setMode("member");
  }, [isMember]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Fresh checkout — drop any stale mode flag from a previous session so the
    // payment step can't inherit a member/bundle choice across accounts (N2).
    localStorage.removeItem("webProfilePurchaseMode");
    if (targetCompanyId) {
      localStorage.setItem("payasyougoCompanyID", JSON.stringify(targetCompanyId));
    }
  }, [targetCompanyId]);

  const effectiveMode = isMember ? "member" : mode;
  const summary = buildWebProfileSummary({ mode: effectiveMode, plan });
  const companyName = company?.registered_company_name || "this company";

  const handleContinue = () => {
    const planDetails = { mode: effectiveMode, plan };
    if (token) {
      navigate("/plans/paymentdetails", {
        state: { planDetails, company_id: targetCompanyId },
      });
    } else {
      localStorage.setItem("lastVisited", "/plans");
      navigate("/login");
    }
  };

  const handleCancel = () => navigate(-1);

  const selectBundlePlan = (nextPlan) => {
    setMode("bundle");
    setPlan(nextPlan);
  };

  return (
    <div className="text-left py-8">
      <nav className="text-f12 text-secondary mb-4 flex items-center gap-2 flex-wrap">
        <a href="/" className="hover:text-primaryText">Home</a>
        <span>›</span>
        {companyName !== "this company" && (
          <>
            <span className="text-primaryText">{companyName}</span>
            <span>›</span>
          </>
        )}
        <span className="text-primaryText font-medium">Buy a Web Profile</span>
      </nav>
      <h1 className="text-f24 md:text-f32 font-bold text-primaryText">Buy a Web Profile</h1>
      <p className="text-secondary text-f16 mt-2 max-w-[640px]">
        Unlock {companyName}'s full profile — financials, ownership, documents — for{" "}
        {PROFILE_WINDOW_DAYS} days.{" "}
        {isMember
          ? "Your Member rate is applied automatically."
          : "Pay the standard rate, or take the Member rate by joining Membership."}
      </p>

      <div className="mt-6 flex flex-col lg:flex-row gap-4 items-start">
        {/* Left: plan cards */}
        <div className="flex-1 w-full">
          {isMember ? (
            <div className="bg-white rounded-lg border-2 border-primary p-6">
              <div className="flex justify-between items-start">
                <h2 className="font-semibold text-f18 text-primaryText">Web Profile</h2>
                <span className="bg-secondary text-link text-f12 px-2 py-1 rounded">
                  Member rate applied
                </span>
              </div>
              <p className="text-secondary text-f14 mt-1">
                Your active membership unlocks the Member rate on this purchase.
              </p>
              <FeatureList features={PROFILE_FEATURES} />
              <div className="mt-4 flex items-center gap-2">
                <span className="text-f24 font-bold text-primaryText">
                  <Aed value={WEB_PROFILE_MEMBER} />
                </span>
                <span className="text-secondary text-f14">/ {PROFILE_WINDOW_DAYS} days</span>
                <span className="text-secondary line-through text-f14">
                  <Aed value={WEB_PROFILE_STANDARD} />
                </span>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4 items-stretch">
              {/* Card 1 — Web Profile (standard) */}
              <button
                type="button"
                onClick={() => setMode("payg")}
                className={`flex flex-col text-left bg-white rounded-lg p-6 border-2 transition ${
                  mode === "payg" ? "border-link" : "border-gray-200"
                }`}
              >
                <div className="flex justify-between items-start">
                  <h2 className="font-semibold text-f18 text-primaryText">Web Profile</h2>
                  <RadioDot selected={mode === "payg"} />
                </div>
                <p className="text-secondary text-f14 mt-1">
                  Standard rate. Pay once, {PROFILE_WINDOW_DAYS} days of full access.
                </p>
                <FeatureList features={PROFILE_FEATURES} />
                <div className="mt-auto pt-4">
                  <span className="text-f24 font-bold text-primaryText">
                    <Aed value={WEB_PROFILE_STANDARD} />
                  </span>
                  <span className="text-secondary text-f14"> / {PROFILE_WINDOW_DAYS} days</span>
                  <p className="text-secondary text-f12">One-time purchase</p>
                </div>
              </button>

              {/* Card 2 — Web Profile + Membership (bundle) */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setMode("bundle")}
                onKeyDown={(e) => e.key === "Enter" && setMode("bundle")}
                className={`relative bg-white rounded-lg p-6 border-2 cursor-pointer transition ${
                  mode === "bundle" ? "border-primary" : "border-gray-200"
                }`}
              >
                <span className="absolute -top-3 left-4 bg-button text-white text-f10 px-2 py-1 rounded">
                  SAVE WITH MEMBERSHIP
                </span>
                <div className="flex justify-between items-start">
                  <h2 className="font-semibold text-f18 text-primaryText">
                    Web Profile + Membership
                  </h2>
                  <RadioDot selected={mode === "bundle"} />
                </div>
                <p className="text-secondary text-f14 mt-1">
                  Pay the Member rate. Membership unlocks the rate on every TREVEX product.
                </p>
                <FeatureList features={MEMBERSHIP_FEATURES} />

                <div className="mt-4">
                  <span className="text-f24 font-bold text-primaryText">
                    <Aed value={WEB_PROFILE_MEMBER} />
                  </span>
                  <span className="text-secondary text-f14"> / {PROFILE_WINDOW_DAYS} days</span>
                </div>

                {/* Annual / Monthly toggle */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectBundlePlan(PLAN_YEARLY);
                    }}
                    className={`relative text-left rounded-md border p-3 pt-6 ${
                      plan === PLAN_YEARLY ? "border-link bg-secondary" : "border-gray-200"
                    }`}
                  >
                    <span className="absolute top-1.5 right-1.5 bg-button text-white text-[10px] leading-none font-semibold px-1.5 py-1 rounded">
                      SAVE 2 MONTHS
                    </span>
                    <p className="text-f14 font-semibold text-primaryText">Annual</p>
                    <p className="text-secondary text-f12">
                      <Aed value={MEMBERSHIP_ANNUAL} /> / yr
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      selectBundlePlan(PLAN_MONTHLY);
                    }}
                    className={`text-left rounded-md border p-3 pt-6 ${
                      plan === PLAN_MONTHLY ? "border-link bg-secondary" : "border-gray-200"
                    }`}
                  >
                    <p className="text-f14 font-semibold text-primaryText">Monthly</p>
                    <p className="text-secondary text-f12">
                      <Aed value={MEMBERSHIP_MONTHLY} /> / mo
                    </p>
                  </button>
                </div>

                <p className="text-secondary text-f12 mt-3 inline-flex flex-wrap items-center gap-1">
                  + <Aed value={plan === PLAN_YEARLY ? MEMBERSHIP_ANNUAL : MEMBERSHIP_MONTHLY} />/
                  {plan === PLAN_YEARLY ? "year" : "month"} —{" "}
                  {plan === PLAN_YEARLY ? "Annual Membership · two months free" : "Monthly Membership"}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end items-center gap-6 mt-6">
            <button
              type="button"
              onClick={handleCancel}
              className="text-primaryText text-f14"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleContinue}
              className="bg-primary text-white text-f14 rounded-md px-6 py-2"
            >
              Continue
            </button>
          </div>
        </div>

        {/* Right: summary */}
        <WebProfileSummary
          company={company}
          summary={summary}
          upsell={!isMember && mode === "payg"}
          onSwitchToMembership={() => setMode("bundle")}
        />
      </div>
    </div>
  );
};
